// pages/Personal Attendance/MyAttendanceSchedule.js
//
// Legacy menus:
//   "Show My FTO Schedule"  → kind="FTO"
//   "Show My COA Schedule"  → kind="COA"
//
// Simple month-grid calendar. Future-only by default. Caller's own
// records (no subordinates) via /dept?mine=true.
//
// One component, two exports — keeps the layout and styling in one
// place so the FTO and COA versions stay visually identical.

import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody, Button, Alert, Badge } from "reactstrap";
import { DashboardPageHeader, TableSkeleton } from "../../Components/Common/DashboardKit";
import { getFtoDept, getCoaDept } from "./ftoService";

// Month-grid helpers
const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const startOfMonth = (y, m) => new Date(y, m, 1);
const endOfMonth   = (y, m) => new Date(y, m + 1, 0);
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// True iff `d` falls inside [start, end] (inclusive). end may be null,
// in which case the record covers only the start day.
const inRange = (d, start, end) => {
    if (!start) return false;
    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = end ? new Date(end) : new Date(start);
    e.setHours(23, 59, 59, 999);
    return d >= s && d <= e;
};

const ScheduleCalendar = ({ kind, fetcher, title, breadcrumb }) => {
    const [today] = useState(() => new Date());
    const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true); setError(null);
        // Pull only the caller's own records via mine=true. The viewer
        // can scroll to past months; we fetch the year of the current
        // view so we never show stale data when paging.
        fetcher({ mine: true, year: view.y })
            .then(({ data }) => {
                if (!alive) return;
                setRows(Array.isArray(data) ? data : (data?.items ?? []));
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.error || e?.message || "Failed to load"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [view.y, fetcher]);

    // Calendar grid: weeks of the chosen month. Leading blanks for
    // the days of the previous month that fall in the first week.
    const cells = useMemo(() => {
        const first = startOfMonth(view.y, view.m);
        const last  = endOfMonth(view.y, view.m);
        const out = [];
        for (let i = 0; i < first.getDay(); i++) out.push(null);
        for (let d = 1; d <= last.getDate(); d++) out.push(new Date(view.y, view.m, d));
        while (out.length % 7 !== 0) out.push(null);
        return out;
    }, [view]);

    // Build a per-day lookup of records covering that day.
    const byDay = useMemo(() => {
        const m = new Map();
        for (const r of rows) {
            for (const cell of cells) {
                if (!cell) continue;
                if (inRange(cell, r.startDate, r.endDate)) {
                    const key = cell.toISOString().slice(0, 10);
                    if (!m.has(key)) m.set(key, []);
                    m.get(key).push(r);
                }
            }
        }
        return m;
    }, [rows, cells]);

    const goPrev = () => setView(v => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 });
    const goNext = () => setView(v => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 });

    const monthLabel = `${MONTH_NAMES[view.m]} ${view.y}`;

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader title={title} subtitle={`My ${kind} schedule`} breadcrumbItems={breadcrumb} />
                {error && <Alert color="danger" className="mt-2">{error}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Button color="light" size="sm" onClick={goPrev}>← Prev</Button>
                            <h5 className="mb-0">{monthLabel}</h5>
                            <Button color="light" size="sm" onClick={goNext}>Next →</Button>
                        </div>

                        {loading ? (
                            <div className="table-responsive">
                                <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed" }}>
                                    <thead className="table-light">
                                        <tr>{["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <th key={d} style={{ width: "14.28%" }}>{d}</th>)}</tr>
                                    </thead>
                                    <tbody><TableSkeleton rows={5} columns={7} height={60} /></tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed" }}>
                                    <thead className="table-light">
                                        <tr>
                                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                                                <th key={d} style={{ width: "14.28%" }}>{d}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: cells.length / 7 }).map((_, wi) => (
                                            <tr key={wi}>
                                                {cells.slice(wi * 7, wi * 7 + 7).map((cell, di) => {
                                                    if (!cell) return <td key={di} style={{ background: "#fafafa" }}></td>;
                                                    const key = cell.toISOString().slice(0, 10);
                                                    const records = byDay.get(key) || [];
                                                    const isToday = sameDay(cell, today);
                                                    return (
                                                        <td key={di} style={{
                                                            verticalAlign: "top", height: 90,
                                                            background: isToday ? "#eef5ff" : undefined,
                                                        }}>
                                                            <div className="text-end small text-muted">{cell.getDate()}</div>
                                                            {records.map((r, i) => (
                                                                <Badge key={i} color="info" pill
                                                                    style={{ fontSize: 10, display: "block", marginTop: 2 }}>
                                                                    {kind} {r.type ? `(${r.type})` : ""}
                                                                </Badge>
                                                            ))}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export const FtoSchedule = () => (
    <ScheduleCalendar
        kind="FTO"
        title="My FTO Schedule"
        fetcher={getFtoDept}
        breadcrumb={[{ label: "Attendance", to: "/workflows?wf=pac" }, { label: "My FTO Schedule" }]}
    />
);
export const CoaSchedule = () => (
    <ScheduleCalendar
        kind="COA"
        title="My COA Schedule"
        fetcher={getCoaDept}
        breadcrumb={[{ label: "Attendance", to: "/workflows?wf=pac" }, { label: "My COA Schedule" }]}
    />
);
export default FtoSchedule;
